import { blobDataURL as readAudio } from "../adapters/blob-data-url";
import { chooseSaveDestination } from "./native-save-destination";
import { prepareFonts } from "../render/native-fonts";
export { prepareFonts } from "../render/native-fonts";
import { activePage } from "../native/pages";
import type { ServerTemplate } from "./native-template-record";
import type { NativeSession } from "../native/session";
import { assertNativeDocument, emptyNativeDocument } from "../native/document";
import { IndexedDbRecoveryStore } from "../adapters/indexeddb-recovery";
import { RecoveryRepository } from "../storage/recovery";
import {
  encodeStoredDocument,
  decodeStoredDocument,
} from "../storage/project-record";
import { uploadDocumentMedia } from "../storage/media-upload";
import { NativeCanvasRenderer } from "../render/native-canvas";
import { ImageStore, readImageFile } from "../render/image-store";
import type { EngineDocument } from "../scene/model";
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
export async function exportNativeImage(
  snapshot: EngineDocument,
  format: "png" | "jpeg",
  images: ImageStore,
  scale = 2,
) {
  await images.prepare(snapshot.assets);
  await prepareFonts(snapshot);
  const size = activePage(snapshot).size;
  if (
    size.width * size.height * scale * scale > 64_000_000 ||
    Math.max(size.width, size.height) * scale > 32768
  )
    throw new Error("Размер страницы слишком велик для экспорта 2×.");
  const canvas = document.createElement("canvas"),
    renderer = new NativeCanvasRenderer(canvas, images);
  renderer.draw(
    {
      document:
        format === "jpeg"
          ? {
              ...snapshot,
              pages: [{ ...activePage(snapshot), backgroundVisible: true }],
            }
          : snapshot,
      camera: { x: 0, y: 0, zoom: scale },
      width: size.width * scale,
      height: size.height * scale,
      dpr: 1,
    },
    true,
    undefined,
    false,
    true,
  );
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) =>
        b ? resolve(b) : reject(new Error("Не удалось создать изображение.")),
      "image/" + format,
      0.95,
    ),
  );
  renderer.dispose();
  return blob;
}
interface ApiProject {
  id: number | string;
  title: string;
  project_data?: unknown;
  data?: unknown;
}
interface ApiResult {
  templates?: ServerTemplate[];
  template?: ServerTemplate;
  ok?: boolean;
  error?: string;
  csrfToken?: string;
  user?: { isAdmin?: boolean };
  project?: ApiProject;
  projects?: ApiProject[];
  version?: { id: number; data: unknown };
  publication?: { teacher_slug: string; project_slug: string };
  url?: string;
}
export class NativeFiles {
  private repository = new RecoveryRepository(
    new IndexedDbRecoveryStore(globalThis.indexedDB, {
      database: "creatorenginepro-native-recovery-v1",
      store: "drafts",
      key:
        document.getElementById("native-editor")?.dataset.mode === "interactive"
          ? "interactive"
          : "current",
    }),
  );
  private ready = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private last = "";
  private csrf = "";
  private project: string | null = new URLSearchParams(location.search).get(
    "project",
  );
  private projectGeneration = 0;
  private saving = false;
  constructor(
    private session: NativeSession,
    private images: ImageStore,
    private blocked: () => boolean,
    private finalize: () => void,
    private changed: () => void,
    private fit: () => void,
    private message: (text: string, error?: boolean) => void,
  ) {
    this.projectGeneration = session.generation;
    for (const format of ["png", "jpeg"] as const)
      document.getElementById("native-save-" + format)!.onclick = async () => {
        this.finalize();
        if (this.blocked()) return;
        this.closeMenu();
        try {
          const snapshot = structuredClone(session.document);
          const destination = await chooseSaveDestination(
            "Изображение",
            format === "jpeg" ? ".jpg" : ".png",
            "image/" + format,
          );
          if (!destination) {
            message("Сохранение отменено.");
            return;
          }
          await destination.write(
            await exportNativeImage(snapshot, format, images),
          );
          message("Изображение сохранено.");
        } catch (e) {
          message((e as Error).message, true);
        }
      };
    document.getElementById("native-open-menu")!.onclick = () => {
      this.closeMenu();
      document.getElementById("native-project")!.click();
    };
    const cloudSave = document.getElementById("native-save-cloud")!;
    if (session.mode === "interactive") {
      cloudSave.hidden = false;
      cloudSave.onclick = () => void this.saveCloud();
      const open = document.createElement("button");
      open.id = "native-cloud-open";
      open.textContent = "Открыть из кабинета";
      open.onclick = () => void this.openCloudList();
      document.querySelector(".save-menu-panel")!.append(open);
    } else cloudSave.remove();
    document.addEventListener("pointerdown", (e) => {
      if (!(e.target as HTMLElement).closest("#save-menu")) this.closeMenu();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void this.recover();
    });
    window.addEventListener("pagehide", () => void this.recover());
    void this.restore();
  }
  closeMenu() {
    (document.getElementById("save-menu") as HTMLDetailsElement).open = false;
  }
  observe() {
    if (!this.ready || this.blocked() || this.session.gesturing) return;
    const key = this.session.generation + ":" + this.session.revision;
    if (key === this.last) return;
    this.last = key;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.recover(), 250);
    if (this.projectGeneration !== this.session.generation) {
      this.project = null;
      this.projectGeneration = this.session.generation;
      const url = new URL(location.href);
      url.searchParams.delete("project");
      history.replaceState(null, "", url);
    }
  }
  private async recover() {
    if (!this.ready || this.blocked() || this.session.gesturing) return false;
    try {
      return await this.repository.write(
        encodeStoredDocument(this.session.document),
      );
    } catch (e) {
      this.message(
        "Не удалось сохранить локальное восстановление: " +
          (e as Error).message,
        true,
      );
      return false;
    }
  }
  private async restore() {
    const generation = this.session.generation,
      revision = this.session.revision;
    try {
      if (this.project) {
        await this.loadCloud(this.project);
        return;
      }
      const record = (await this.repository.read()) as {
        projectData?: unknown;
      } | null;
      if (!record?.projectData) return;
      const document = decodeStoredDocument(record.projectData);
      assertNativeDocument(document);
      await this.images.prepareDisplay(document.assets);
      await prepareFonts(document);
      if (
        generation !== this.session.generation ||
        revision !== this.session.revision ||
        this.session.gesturing ||
        this.blocked()
      )
        return;
      this.session.reset(document);
      this.images.retain(document.assets);
      this.session.dirty = true;
      this.fit();
      this.message("Работа восстановлена из локального черновика.");
    } catch (e) {
      this.message((e as Error).message, true);
    } finally {
      this.ready = true;
      this.projectGeneration = this.session.generation;
      this.changed();
    }
  }
  async api(path: string, body?: unknown): Promise<ApiResult> {
    if (body && !this.csrf) await this.api("/session");
    const response = await fetch("/creatorenginepro/api" + path, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        ...(body
          ? { "Content-Type": "application/json", "X-CREATOR-CSRF": this.csrf }
          : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const payload = (await response.json()) as ApiResult;
    if (!response.ok || payload.ok === false)
      throw new Error(payload.error || "Ошибка сохранения на сервере.");
    if (payload.csrfToken) this.csrf = payload.csrfToken;
    return payload;
  }
  async saveCloud() {
    if (this.session.mode !== "interactive") return;
    this.finalize();
    if (this.blocked() || this.saving) return;
    this.closeMenu();
    this.saving = true;
    const snapshot = structuredClone(this.session.document),
      revision = this.session.revision,
      generation = this.session.generation;
    try {
      const auth = await this.api("/session");
      if (!auth.user) {
        if (!(await this.repository.write(encodeStoredDocument(snapshot))))
          throw new Error("Не удалось сохранить черновик перед входом.");
        location.href =
          "/auth/login?back=" +
          encodeURIComponent(
            "/creatorenginepro/?engine=native" +
              (this.session.mode === "interactive" ? "&mode=interactive" : ""),
          );
        return;
      }
      if (!auth.user.isAdmin)
        throw new Error(
          "Сохранение в кабинет пока доступно администратору, как в полной версии редактора.",
        );
      let project = this.project;
      if (!project) {
        const title = prompt("Название проекта", "Новый рабочий лист");
        if (title === null) return;
        const result = await this.api("/projects", {
          action: "create",
          title: title.trim() || "Новый рабочий лист",
          slug: "native-" + crypto.randomUUID(),
        });
        project = String(result.project!.id);
        if (generation === this.session.generation) {
          this.project = project;
          this.projectGeneration = generation;
        }
      }
      const uploaded = await uploadDocumentMedia(snapshot, async (asset) => {
        // Keep SVG inside the document, displayed only through Image; the upload API
        // deliberately does not publish SVG as an executable standalone resource.
        if (/^data:image\/svg\+xml;base64,/i.test(asset.source))
          return asset.source;
        const result = await this.api("/upload", {
          projectId: project,
          kind: asset.kind === "audio" ? "sound" : "image",
          fileName: asset.kind,
          dataUrl: asset.source,
        });
        if (!result.url) throw new Error("Не получен адрес изображения.");
        return result.url;
      });
      const saved = await this.api("/projects", {
        action: "save",
        projectId: project,
        data: encodeStoredDocument(uploaded),
      });
      if (generation === this.session.generation) {
        this.project = project;
        this.projectGeneration = generation;
        const url = new URL(location.href);
        url.searchParams.set("project", project);
        history.replaceState(null, "", url);
        if (
          revision === this.session.revision &&
          !this.session.gesturing &&
          !this.blocked()
        )
          this.session.dirty = false;
      }
      this.message(
        revision === this.session.revision &&
          generation === this.session.generation
          ? "Проект сохранён в кабинет."
          : "Версия сохранена. Последующие изменения ещё не сохранены.",
      );
      return { project, version: saved.version?.id, snapshot, generation };
    } catch (e) {
      this.message((e as Error).message, true);
    } finally {
      this.saving = false;
      this.changed();
    }
  }
  private async embed(document: EngineDocument) {
    for (const a of document.assets) {
      if (a.source.startsWith("data:")) continue;
      const url = new URL(a.source, location.origin);
      if (
        url.origin !== location.origin ||
        !url.pathname.startsWith("/upload/creatorenginepro/")
      )
        throw new Error("Недопустимый адрес изображения проекта.");
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok)
        throw new Error("Не удалось загрузить изображение проекта.");
      const blob = await response.blob();
      a.source =
        a.kind === "audio"
          ? await readAudio(blob)
          : await readImageFile(new File([blob], "image", { type: blob.type }));
    }
    assertNativeDocument(document);
    await this.images.prepareDisplay(document.assets);
    await prepareFonts(document);
    return document;
  }
  private async loadCloud(id: string) {
    const gen = this.session.generation,
      rev = this.session.revision,
      result = await this.api(
        "/projects?action=get&id=" + encodeURIComponent(id),
      );
    const p = result.project;
    if (!p) throw new Error("Проект не найден.");
    const stored = p.project_data ?? p.data,
      document =
        stored == null
          ? emptyNativeDocument()
          : await this.embed(decodeStoredDocument(stored));
    if (
      gen !== this.session.generation ||
      rev !== this.session.revision ||
      this.blocked() ||
      this.session.gesturing
    )
      throw new Error(
        "За время загрузки вы начали другое действие. Повторите открытие.",
      );
    this.session.reset(document);
    this.images.retain(document.assets);
    this.project = id;
    this.projectGeneration = this.session.generation;
    const url = new URL(location.href);
    url.searchParams.set("project", id);
    history.replaceState(null, "", url);
    this.fit();
    this.message("Проект открыт из кабинета.");
  }
  private async openCloudList() {
    if (this.session.mode !== "interactive") return;
    this.finalize();
    if (this.blocked()) return;
    this.closeMenu();
    try {
      const result = await this.api("/projects");
      const dialog = document.createElement("dialog");
      dialog.id = "native-cloud-dialog";
      const title = document.createElement("h2");
      title.textContent = "Мои проекты";
      dialog.append(title);
      for (const p of result.projects ?? []) {
        const b = document.createElement("button");
        b.textContent = p.title;
        b.onclick = () => {
          if (
            this.session.dirty &&
            !confirm(
              "Открыть другой проект? Сохраните текущую работу, если она нужна.",
            )
          )
            return;
          dialog.close();
          dialog.remove();
          void this.loadCloud(String(p.id)).catch((e) =>
            this.message(e.message, true),
          );
        };
        dialog.append(b);
      }
      const close = document.createElement("button");
      close.textContent = "Закрыть";
      close.onclick = () => {
        dialog.close();
        dialog.remove();
      };
      dialog.append(close);
      document.getElementById("native-editor")!.append(dialog);
      dialog.showModal();
    } catch (e) {
      this.message((e as Error).message, true);
    }
  }
}
